import {
  Menu,
  PanelLeft,
  ChevronLeft,
} from "lucide-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@pix2bit/ui/components";
import { SettingsDropdown } from "./Logout";
import { Link } from "react-router-dom";
import { getRouteConfig } from "../../config/routes";
import { menuItems, bottomMenuItem } from "../../config/menuItems";
import { LoadingSpinner } from "../LoadingSpinner";
import { useDelayedValue } from "../../hooks/useDelayedValue";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  const { icon: PageIcon, title } = getRouteConfig(location.pathname);

  return (
    <div className="flex h-screen">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />

      <div className="flex flex-col flex-1 min-w-0 relative z-10">
        <header className="border-b bg-white border-gray-200">
          <div className="flex items-center justify-between p-4 sm:p-5 gap-2">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden flex-shrink-0 text-gray-600"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div className="flex items-center gap-3 min-w-0 flex-1">
                <PageIcon className="h-6 w-6 text-gray-700 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                    {title}
                  </h1>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
              <SettingsDropdown />
              <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-primary flex-shrink-0 hidden sm:block" />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-white">
          <Outlet />
        </main>

      </div>
    </div>
  );
}
function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [optimisticPathname, setOptimisticPathname] = useState(location.pathname);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isOptimisticActive = (path: string) => {
    return optimisticPathname === path;
  };

  const isLoading = (path: string) => {
    return optimisticPathname === path && location.pathname !== path;
  };

  const delayedIsLoading = (path: string) => {
    return useDelayedValue({ value: isLoading(path), delay: 250 });
  };

  const handleClick = (path: string) => {
    setOptimisticPathname(path);
    setSidebarOpen(false);
    setTimeout(() => {
      navigate(path);
    }, 0);
  };

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  useEffect(() => {
    setOptimisticPathname(location.pathname);
  }, [location.pathname]);

  const getLinkClasses = (path: string) => {
    return `flex items-center ${sidebarCollapsed ? "justify-center px-2 py-3" : "gap-3 px-3 py-2.5"}
      text-sm font-medium rounded-lg transition-colors duration-200 ease-linear hover:bg-gray-100 relative
      ${isActive(path) ? "bg-gray-100 text-gray-900" : "text-gray-600"}`;
  };

  return (
    <div
      className={`
      fixed inset-y-0 left-0 bg-[#F8F9FA] border-r border-gray-200 lg:translate-x-0 lg:static lg:inset-0 z-50 lg:z-auto
      ${sidebarOpen ? "translate-x-0 transition-transform duration-200 ease-linear" : "-translate-x-full transition-transform duration-200 ease-linear"}
      ${sidebarCollapsed ? "w-20 transition-all duration-200 ease-linear" : "w-64 transition-all duration-200 ease-linear"}
    `}
    >
      <div className="flex flex-col h-full p-4">
        <div
          className={`flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"} mb-8`}
        >
          <div
            className={`flex items-center ${sidebarCollapsed ? "justify-center w-full" : "gap-3"}`}
          >
            <Link
              to="/dashboard/home"
              className={`flex items-center gap-2 duration-200 ease-linear hover:opacity-80 transition-opacity ${sidebarCollapsed ? "justify-center" : ""}`}
              onClick={() => {
                if (sidebarCollapsed) {
                  setSidebarCollapsed(false);
                }
              }}
            >
              <img
                src="/pix2bit.png"
                alt="Pix2bit"
                className={`${sidebarCollapsed ? "w-12 h-12" : "w-32 h-auto"} object-contain`}
              />
            </Link>
          </div>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleCollapse}
                className="size-7 hidden lg:flex hover:bg-gray-200 text-gray-600"
              >
                <PanelLeft className="h-4 w-4" />
                <span className="sr-only">Toggle Sidebar</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden flex items-center gap-1 hover:bg-gray-200 text-gray-600"
                onClick={() => setSidebarOpen(false)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <nav className="flex-1 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.startIcon;
            const itemPath = item.href || "";
            const showLoading = isLoading(itemPath);
            const delayedShowLoading = useDelayedValue({
              value: showLoading,
              delay: 250,
            });

            return (
              <a
                key={itemPath}
                className={getLinkClasses(itemPath)}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (itemPath) {
                    handleClick(itemPath);
                  }
                }}
                title={sidebarCollapsed ? item.label : ""}
              >
                <Icon
                  className={`${sidebarCollapsed ? "h-5 w-5" : "h-5 w-5"} flex-shrink-0`}
                />
                {!sidebarCollapsed && (
                  <span className="flex-1">{item.label}</span>
                )}
                {!sidebarCollapsed && delayedShowLoading && (
                  <LoadingSpinner />
                )}
                {item.endIcon && !sidebarCollapsed && item.endIcon}
              </a>
            );
          })}
        </nav>

        <div className="mt-auto pt-4 border-t border-gray-200">
          <nav className="space-y-1">
            {(() => {
              const Icon = bottomMenuItem.startIcon;
              const itemPath = bottomMenuItem.href || "";
              const showLoading = isLoading(itemPath);
              const delayedShowLoading = useDelayedValue({
                value: showLoading,
                delay: 250,
              });

              return (
                <a
                  key={itemPath}
                  className={getLinkClasses(itemPath)}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (itemPath) {
                      handleClick(itemPath);
                    }
                  }}
                  title={sidebarCollapsed ? bottomMenuItem.label : ""}
                >
                  <Icon
                    className={`${sidebarCollapsed ? "h-5 w-5" : "h-5 w-5"} flex-shrink-0`}
                  />
                  {!sidebarCollapsed && (
                    <span className="flex-1">{bottomMenuItem.label}</span>
                  )}
                  {!sidebarCollapsed && delayedShowLoading && (
                    <LoadingSpinner />
                  )}
                </a>
              );
            })()}
          </nav>
        </div>
      </div>
    </div>
  );
}
